import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import API from '../../utils/api';
import { ROLE_LABELS, ROLE_COLORS } from '../../utils/roles';

const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };

const DEPARTMENTS = ['OPD', 'IPD', 'Emergency', 'Laboratory', 'Radiology', 'Psychiatry', 'Pharmacy', 'Blood Bank', 'Reception', 'Administration', 'IT', 'Nursing', 'ICU'];

const empty = { employee_id: '', full_name: '', email: '', phone: '', role_id: '', department: '', designation: '', password: '' };

export default function UserManagement() {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');

    const load = async () => {
        try {
            const [u, r] = await Promise.all([API.get('/auth/users'), API.get('/auth/roles')]);
            setUsers(u.data);
            setRoles(r.data);
        } catch { toast.error('Failed to load data'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const openAdd = () => { setEditing(null); setForm(empty); setShowForm(true); };
    const openEdit = (u) => {
        setEditing(u);
        setForm({ employee_id: u.employee_id, full_name: u.full_name, email: u.email || '', phone: u.phone || '', role_id: u.role_id, department: u.department || '', designation: u.designation || '', password: '' });
        setShowForm(true);
    };

    const save = async () => {
        if (!form.full_name || !form.role_id) return toast.error('Full Name and Role are required');
        if (!editing && !form.password) return toast.error('Password is required for new users');
        if (!editing && !form.employee_id) return toast.error('Employee ID is required');
        setSaving(true);
        try {
            if (editing) {
                await API.put(`/auth/users/${editing.id}`, form);
                toast.success('User updated successfully');
            } else {
                await API.post('/auth/users', form);
                toast.success('New user created successfully');
            }
            setShowForm(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error');
        } finally { setSaving(false); }
    };

    const toggleStatus = async (u) => {
        if (!window.confirm(`Are you sure you want to ${u.is_active ? 'deactivate' : 'activate'} ${u.full_name}?`)) return;
        try {
            const res = await API.patch(`/auth/users/${u.id}/toggle`);
            toast.success(res.data.message);
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    };

    const filtered = users.filter(u =>
        u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        u.employee_id?.toLowerCase().includes(search.toLowerCase()) ||
        u.department?.toLowerCase().includes(search.toLowerCase())
    );

    const active = users.filter(u => u.is_active).length;

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>;

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>👥 User Management</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>Manage staff accounts and roles</p>
                </div>
                <button onClick={openAdd}
                    style={{ padding: '10px 22px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    + Add User
                </button>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                    { label: 'Total Staff', value: users.length, color: '#1e3a5f', bg: '#eff6ff' },
                    { label: 'Active', value: active, color: '#059669', bg: '#f0fdf4' },
                    { label: 'Inactive', value: users.length - active, color: '#dc2626', bg: '#fef2f2' },
                ].map(s => (
                    <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '16px 20px', textAlign: 'center' }}>
                        <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Search */}
            <div style={{ marginBottom: 14 }}>
                <input style={{ ...inputStyle, maxWidth: 320 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search by Name, Employee ID or Department..." />
            </div>

            {/* Users Table */}
            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc' }}>
                            {['Employee ID', 'Name', 'Role', 'Department', 'Designation', 'Last Login', 'Status', 'Action'].map(h => (
                                <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No users found</td></tr>
                        ) : filtered.map((u) => {
                            const rc = ROLE_COLORS[u.role] || { bg: '#1e3a5f', text: '#fff' };
                            return (
                                <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: u.is_active ? 1 : 0.5 }}>
                                    <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>{u.employee_id}</td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>{u.full_name}</div>
                                        {u.email && <div style={{ fontSize: 11, color: '#64748b' }}>{u.email}</div>}
                                    </td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <span style={{ background: rc.bg, color: rc.text, borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700 }}>
                                            {ROLE_LABELS[u.role] || u.role_display || u.role}
                                        </span>
                                    </td>
                                    <td style={{ padding: '10px 14px', fontSize: 12 }}>{u.department || '—'}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{u.designation || '—'}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 11, color: '#64748b' }}>
                                        {u.last_login ? new Date(u.last_login).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Never'}
                                    </td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <span style={{ background: u.is_active ? '#dcfce7' : '#fee2e2', color: u.is_active ? '#166534' : '#dc2626', borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700 }}>
                                            {u.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '10px 14px', display: 'flex', gap: 6 }}>
                                        <button onClick={() => openEdit(u)}
                                            style={{ padding: '5px 10px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                            ✏️ Edit
                                        </button>
                                        <button onClick={() => toggleStatus(u)}
                                            style={{ padding: '5px 10px', background: u.is_active ? '#dc2626' : '#059669', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                            {u.is_active ? 'Deactivate' : 'Activate'}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Add/Edit Modal */}
            {showForm && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: 14, padding: 28, width: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>
                                {editing ? `✏️ Edit: ${editing.full_name}` : '+ Add New User'}
                            </h3>
                            <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Employee ID *</label>
                                <input style={{ ...inputStyle, background: editing ? '#f8fafc' : 'white' }} value={form.employee_id} onChange={e => setF('employee_id', e.target.value)} placeholder="e.g. EMP-007" disabled={!!editing} />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Full Name *</label>
                                <input style={inputStyle} value={form.full_name} onChange={e => setF('full_name', e.target.value)} placeholder="e.g. Dr. Ahmed Ali" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Role *</label>
                                <select style={{ ...inputStyle, background: 'white' }} value={form.role_id} onChange={e => setF('role_id', e.target.value)}>
                                    <option value="">-- Select Role --</option>
                                    {roles.map(r => <option key={r.id} value={r.id}>{r.display_name || r.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Department</label>
                                <select style={{ ...inputStyle, background: 'white' }} value={form.department} onChange={e => setF('department', e.target.value)}>
                                    <option value="">-- Select --</option>
                                    {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Designation</label>
                                <input style={inputStyle} value={form.designation} onChange={e => setF('designation', e.target.value)} placeholder="e.g. Senior Doctor" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Phone</label>
                                <input style={inputStyle} value={form.phone} onChange={e => setF('phone', e.target.value)} placeholder="03xx-xxxxxxx" />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Email</label>
                                <input style={inputStyle} value={form.email} onChange={e => setF('email', e.target.value)} placeholder="email@hospital.com" />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                    Password {editing ? '(leave blank to keep current password)' : '*'}
                                </label>
                                <input type="password" style={inputStyle} value={form.password} onChange={e => setF('password', e.target.value)} placeholder={editing ? 'New password (optional)' : 'Set password'} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button onClick={() => setShowForm(false)} style={{ padding: '9px 20px', background: '#f1f5f9', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
                            <button onClick={save} disabled={saving}
                                style={{ padding: '9px 24px', background: saving ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                {saving ? 'Saving...' : editing ? '✅ Update User' : '✅ Create User'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
